import { z } from "zod";
import { Currency } from "./model";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/src/components/ui/form";
import { Button } from "@/src/components/ui/button";
import { useToast } from "@/src/components/ui/use-toast";
import { useFetch } from "@/src/lib/useFetch";
import { useDialogClose } from "@/src/components/ui/dialog";
import { useEmit } from "@/src/lib/actions";
import { InputNumber } from "@/src/components/ui/input-number";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/src/components/ui/select";
import { Input } from "@/src/components/ui/input";

const schema = z.object({
  code: z.string(),
  overrideRate: z.number().nullable(),
  threshold: z.number().min(0),
  precisionDigits: z.number().int(),
});

type CurrencyFormValue = z.infer<typeof schema>;

export type EditCurrencyFormProps = { currency: Currency };
export function EditCurrencyForm({ currency }: EditCurrencyFormProps) {
  const { toast } = useToast();
  const { fetch, isFetching } = useFetch();
  const close = useDialogClose();
  const emitUpdated = useEmit("currencyUpdated");

  const form = useForm<CurrencyFormValue>({
    resolver: zodResolver(schema),
    defaultValues: {
      code: currency.code,
      overrideRate: currency.overrideRate ? currency.rate : null,
      precisionDigits: currency.precisionDigits,
      threshold: currency.threshold,
    },
  });
  const isChanged = form.formState.isDirty;

  const onSubmit = form.handleSubmit(async (data) => {
    try {
      if (isChanged) {
        await fetch(`/api/currencies/config`, "PATCH", [data]);
        emitUpdated();
        toast({
          title: "Currency saved",
          description: "Currency has been saved successfully",
        });
      }
    } catch (error: any) {
      const message = "message" in error ? error.message : "An error occurred";
      toast({
        title: "Failed to save currency",
        description: message,
        variant: "destructive",
      });
    } finally {
      close();
    }
  }, console.error);

  return (
    <Form {...form}>
      <form className="flex flex-col gap-6">
        <FormField<CurrencyFormValue, "overrideRate">
          name="overrideRate"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Override rate</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  value={field.value || undefined}
                  onChange={(ev) => {
                    const s = ev.currentTarget.value;
                    if (s === "") {
                      field.onChange(null);
                    }
                    if (!isNaN(+s)) {
                      field.onChange(+s);
                    }
                  }}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField<CurrencyFormValue, "threshold">
          name="threshold"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Threshold</FormLabel>
              <FormControl>
                <InputNumber {...field} />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField<CurrencyFormValue, "precisionDigits">
          name="precisionDigits"
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <FormLabel>Precision</FormLabel>
              <Select onValueChange={(v) => onChange(+v)} defaultValue={value.toString()}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="-2">-2</SelectItem>
                  <SelectItem value="-1">-1</SelectItem>
                  <SelectItem value="0">0</SelectItem>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                  <SelectItem value="4">4</SelectItem>
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />
        <Button onClick={onSubmit} disabled={isFetching} type="submit">
          Save
        </Button>
      </form>
    </Form>
  );
}
